import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button as MuiButton,
  Link,
  Chip,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { createExcelTemplate } from "./template";
import Button from "../../../common/Button";

const AddCSV = ({
  handleNextStep,
  handleBackStep,
  csvData,
  handleDrop,
  file,
  onRedirectToGroups,
  availableUserTypes = [],
  availableGroups = [],
}) => {
  const [download, setDownload] = useState(false);

  // File is uploaded status
  const isFileUploaded = file !== null && csvData.length > 0;

  // Custom onDrop handler that ensures the input is always enabled for re-uploads
  const onDrop = (acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      handleDrop(acceptedFiles);
    }
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: isFileUploaded,
    noKeyboard: isFileUploaded,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
    },
  });

  // Handler to clear the current file and allow for a new upload
  const handleClearFile = (e) => {
    e.stopPropagation();
    handleDrop(null);
  };

  return (
    <div>
      <div>
        <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
          Step 1: Download CSV Template
        </Typography>

        <Button
          onClick={() => {
            createExcelTemplate();
            setDownload(true);
          }}
          text={"Download Template"}
        />

        {/* Instructions */}
        <Box sx={{ mt: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Available User Types:</strong>
          </Typography>
          <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {availableUserTypes.map((type) => (
              <Chip
                key={type.id}
                label={type.title}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>

          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Available Groups:</strong>
          </Typography>
          <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {availableGroups.map((group) => (
              <Chip
                key={group.id}
                label={group.name}
                size="small"
                color="secondary"
                variant="outlined"
              />
            ))}
          </Box>

          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Important Notes:</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: "#666" }}>
            • User types and group names in your CSV must match exactly with the
            available options above
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: "#666" }}>
            • Names are case-insensitive but must be spelled correctly
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: "#666" }}>
            • Each user can have different user types and belong to different
            groups
          </Typography>

          <Typography variant="body2">
            <strong>New Users can only be added to pre-existing Groups.</strong>{" "}
            To view and manage existing groups, please{" "}
            <Link
              component="button"
              variant="body2"
              onClick={onRedirectToGroups}
              sx={{ color: "primary.main", cursor: "pointer" }}
            >
              click here
            </Link>
            .
          </Typography>
        </Box>

        <Box
          sx={{
            pointerEvents: download ? "auto" : "none",
            opacity: download ? 1 : 0.5,
            mt: 3,
          }}
        >
          <div
            {...getRootProps()}
            style={{
              border: "1px dashed gray",
              padding: "20px",
              marginTop: "20px",
              color: download ? "black" : "gray",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              cursor: download && !isFileUploaded ? "pointer" : "default",
              backgroundColor: isFileUploaded ? "#f0f7ff" : "transparent",
              position: "relative",
            }}
          >
            <input {...getInputProps()} />

            {isFileUploaded ? (
              <>
                <CheckCircleIcon
                  style={{ color: "#4caf50", fontSize: 40, marginBottom: 10 }}
                />
                <Typography
                  variant="body1"
                  style={{ fontWeight: "500", marginBottom: 5 }}
                >
                  File uploaded successfully
                </Typography>
                <Paper
                  elevation={1}
                  style={{
                    padding: "8px 16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginTop: 10,
                    backgroundColor: "#e8f4ff",
                    width: "80%",
                    maxWidth: "350px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <InsertDriveFileIcon
                      style={{ marginRight: 10, color: "#1976d2" }}
                    />
                    <Typography variant="body2">
                      {file?.name} ({csvData.length} users)
                    </Typography>
                  </div>
                  <MuiButton
                    size="small"
                    style={{ minWidth: "auto", padding: "4px" }}
                    onClick={handleClearFile}
                  >
                    <DeleteOutlineIcon fontSize="small" color="error" />
                  </MuiButton>
                </Paper>

                <div style={{ marginTop: 20, display: "flex", gap: 8 }}>
                  <MuiButton
                    variant="outlined"
                    size="small"
                    startIcon={<UploadFileIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      open();
                    }}
                    style={{ textTransform: "none" }}
                  >
                    Upload another file
                  </MuiButton>
                </div>
              </>
            ) : (
              <>
                <UploadFileIcon style={{ fontSize: 40, marginBottom: 10 }} />
                <Typography variant="body1">
                  Drag and drop or click to upload a CSV/Excel file
                </Typography>
                <Typography
                  variant="body2"
                  style={{ marginTop: 5, color: "#666" }}
                >
                  Supported formats: .csv, .xls, .xlsx
                </Typography>
              </>
            )}
          </div>
        </Box>
      </div>

      <div style={{ display: "flex", flexDirection: "column", marginTop: 16 }}>
        <Button
          variant="contained"
          onClick={() => {
            handleNextStep();
          }}
          disabled={!csvData.length}
          text={isFileUploaded ? "Continue" : "Upload CSV file"}
        />
      </div>
    </div>
  );
};

export default AddCSV;
